import { useMemo } from "react";
import { Marker } from "react-map-gl/maplibre";
import useSupercluster from "use-supercluster";
import type { PointFeature } from "supercluster";
import type { BBox, Restroom } from "../types";
import { useT } from "../i18n";

interface Props {
  restrooms: Restroom[];
  bounds: BBox | null;
  zoom: number;
  highlightId?: number | null;
  onSelect: (r: Restroom) => void;
  onClusterClick: (lng: number, lat: number, zoom: number) => void;
}

type RestroomPointProps = { cluster: false; restroomId: number };

/**
 * Renderuje markery WC a shlukuje je přes supercluster.
 * Body i clustery jsou DOM markery react-map-gl (emoji piny zůstávají).
 */
export default function ClusteredRestroomMarkers({
  restrooms,
  bounds,
  zoom,
  highlightId,
  onSelect,
  onClusterClick,
}: Props) {
  const t = useT();
  const byId = useMemo(() => new Map(restrooms.map((r) => [r.id, r])), [restrooms]);

  const points: Array<PointFeature<RestroomPointProps>> = useMemo(
    () =>
      restrooms.map((r) => ({
        type: "Feature",
        properties: { cluster: false, restroomId: r.id },
        geometry: { type: "Point", coordinates: [r.lng, r.lat] },
      })),
    [restrooms],
  );

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds: bounds ?? undefined,
    zoom,
    options: { radius: 60, maxZoom: 17 },
  });

  return (
    <>
      {clusters.map((cluster) => {
        const [lng, lat] = cluster.geometry.coordinates;
        const props = cluster.properties;

        if (props.cluster) {
          const count = props.point_count;
          const size = count < 10 ? 34 : count < 100 ? 42 : 52;
          return (
            <Marker
              key={`cluster-${cluster.id}`}
              longitude={lng}
              latitude={lat}
              anchor="center"
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                const expansionZoom = Math.min(
                  supercluster!.getClusterExpansionZoom(cluster.id as number),
                  18,
                );
                onClusterClick(lng, lat, expansionZoom);
              }}
            >
              <div className="cluster" style={{ width: size, height: size }}>
                {count}
              </div>
            </Marker>
          );
        }

        const restroom = byId.get(props.restroomId);
        if (!restroom) return null;
        return (
          <Marker
            key={`r-${restroom.id}`}
            longitude={lng}
            latitude={lat}
            anchor="center"
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              onSelect(restroom);
            }}
          >
            <span
              className={highlightId === restroom.id ? "pin highlight" : "pin"}
              title={restroom.name ?? t("detail.defaultName")}
            >
              🚻
            </span>
          </Marker>
        );
      })}
    </>
  );
}
